/**
 * My Hunts personal chart helpers (field-edition time-of-day bars).
 */

const TOD_LABELS = {
  morning: 'AM',
  midday: 'MID',
  afternoon: 'PM',
  evening: 'EVE',
  night: 'NIGHT',
  all_day: 'ALL',
};

/**
 * Short mono label for a time-period key.
 * @param {string} period
 * @returns {string}
 */
export function formatTodLabel(period) {
  if (!period) return '—';
  const key = String(period).trim().toLowerCase().replace(/\s+/g, '_');
  if (TOD_LABELS[key]) return TOD_LABELS[key];
  return String(period)
    .trim()
    .replace(/_/g, ' ')
    .slice(0, 6)
    .toUpperCase();
}

/**
 * Display name for best-window insight (sentence case).
 * @param {string} period
 * @returns {string}
 */
export function formatTodInsightLabel(period) {
  if (!period) return 'unknown';
  return String(period)
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build enamel bar model from `stats.time_period_breakdown`.
 * @param {Array<{ time_period?: string, deer_seen?: number }>} breakdown
 * @returns {{ bars: Array, bestLabel: string|null, bestPct: number|null, total: number }}
 */
export function buildTimeOfDayBarModel(breakdown) {
  const rows = Array.isArray(breakdown) ? breakdown : [];
  const barsRaw = rows
    .map((row) => {
      const value = Number(row?.deer_seen) || 0;
      const period = row?.time_period || '';
      return {
        key: period || `tod-${value}`,
        period,
        label: formatTodLabel(period),
        value,
      };
    })
    .filter((b) => b.period || b.value > 0);

  const total = barsRaw.reduce((sum, b) => sum + b.value, 0);
  const max = barsRaw.reduce((m, b) => Math.max(m, b.value), 0);

  const bars = barsRaw.map((b) => {
    let tone = 'base';
    if (max > 0 && b.value === max) tone = 'peak';
    else if (max > 0 && b.value > max * 0.5) tone = 'strong';
    const heightPct = max > 0 ? Math.max(8, Math.round((b.value / max) * 100)) : 8;
    return { ...b, tone, heightPct };
  });

  if (total <= 0 || max <= 0) {
    return { bars, bestLabel: null, bestPct: null, total };
  }

  const peak = barsRaw.find((b) => b.value === max);
  const bestPct = Math.round((max / total) * 100);
  return {
    bars,
    bestLabel: formatTodInsightLabel(peak?.period),
    bestPct,
    total,
  };
}
